import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { getStripeProducts, getStripePrices, createStripePrice, updateStripeProduct } from '../../api/billingProducts';

const fmtAmount = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export const ProductsManager: React.FC = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [prices, setPrices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editModal, setEditModal] = useState<{ open: boolean; product?: any }>({ open: false });
  const [editForm, setEditForm] = useState({ name: '', description: '', active: true });
  const [newPriceModal, setNewPriceModal] = useState<{ open: boolean; productId?: string }>({ open: false });
  const [newPriceForm, setNewPriceForm] = useState({ amount: '', recurring: true, interval: 'month' });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prods, prics] = await Promise.all([getStripeProducts(), getStripePrices()]);
      setProducts(prods);
      setPrices(prics);
    } catch (e) {
      console.error('Failed to load Stripe products:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleEditSave = async () => {
    if (!editModal.product?.id) return;
    setSaving(true);
    try {
      await updateStripeProduct(editModal.product.id, {
        name: editForm.name,
        description: editForm.description,
        active: editForm.active,
      });
      setEditModal({ open: false });
      await fetchData();
    } finally {
      setSaving(false);
    }
  };

  const handleNewPrice = async () => {
    if (!newPriceModal.productId) return;
    const cents = Math.round(parseFloat(newPriceForm.amount) * 100);
    if (isNaN(cents) || cents <= 0) return;
    setSaving(true);
    try {
      await createStripePrice(
        newPriceModal.productId,
        cents,
        newPriceForm.recurring ? { interval: newPriceForm.interval } : undefined
      );
      setNewPriceModal({ open: false });
      await fetchData();
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading Stripe products...</div>;

  return (
    <div>
      <div className="divide-y divide-gray-100">
        <div className="grid grid-cols-6 gap-2 px-4 py-2 text-xs font-medium text-gray-500 uppercase bg-gray-50">
          <span>Product</span>
          <span>ID</span>
          <span>Type</span>
          <span>Default Price</span>
          <span>Status</span>
          <span>Actions</span>
        </div>
        {products.map(prod => {
          const prodPrices = prices.filter(p => p.product === prod.id);
          const defaultPrice = prodPrices.find(p => p.id === prod.default_price) || prodPrices[0];
          const isExpanded = expandedId === prod.id;

          return (
            <div key={prod.id}>
              <div className="grid grid-cols-6 gap-2 px-4 py-3 text-sm items-center cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedId(isExpanded ? null : prod.id)}>
                <span className="font-medium text-gray-900">{prod.name}</span>
                <span className="font-mono text-xs text-gray-500 truncate" title={prod.id}>{prod.id.slice(0, 20)}...</span>
                <span className="text-gray-600">{defaultPrice?.recurring ? `Recurring (${defaultPrice.recurring.interval})` : 'One-time'}</span>
                <span className="font-medium">{defaultPrice ? fmtAmount(defaultPrice.unit_amount) : '—'}</span>
                <span><Badge color={prod.active ? 'green' : 'red'}>{prod.active ? 'Active' : 'Archived'}</Badge></span>
                <span className="flex gap-2" onClick={e => e.stopPropagation()}>
                  <button className="text-xs text-brand-600 hover:underline"
                    onClick={() => { setEditForm({ name: prod.name, description: prod.description || '', active: prod.active }); setEditModal({ open: true, product: prod }); }}>
                    Edit
                  </button>
                  <button className="text-xs text-brand-600 hover:underline"
                    onClick={() => { setNewPriceForm({ amount: '', recurring: true, interval: 'month' }); setNewPriceModal({ open: true, productId: prod.id }); }}>
                    + Price
                  </button>
                </span>
              </div>
              {isExpanded && (
                <div className="bg-gray-50 px-8 py-3 border-t border-gray-100">
                  {prod.description && <p className="text-xs text-gray-500 mb-2">{prod.description}</p>}
                  <p className="text-xs font-medium text-gray-500 mb-2">Prices ({prodPrices.length})</p>
                  {prodPrices.length === 0 ? (
                    <p className="text-xs text-gray-400">No prices configured</p>
                  ) : (
                    <div className="space-y-1">
                      {prodPrices.map(price => (
                        <div key={price.id} className="flex justify-between text-sm">
                          <span className="text-gray-700">
                            {price.nickname || (price.recurring ? `${price.recurring.interval}ly` : 'One-time')}
                            {price.id === prod.default_price && <> <Badge color="blue">Default</Badge></>}
                          </span>
                          <div className="flex items-center gap-3">
                            <span className="font-medium">{fmtAmount(price.unit_amount)}{price.recurring ? `/${price.recurring.interval}` : ''}</span>
                            <span className="font-mono text-xs text-gray-400">{price.id}</span>
                            <Badge color={price.active ? 'green' : 'red'}>{price.active ? 'Active' : 'Archived'}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-400">Metadata: {JSON.stringify(prod.metadata || {})}</p>
                    <p className="text-xs text-gray-400">Created: {prod.created ? new Date(prod.created * 1000).toLocaleDateString() : '—'}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit Product Modal */}
      <Modal open={editModal.open} onClose={() => setEditModal({ open: false })} title="Edit Product">
        <Input label="Product Name" value={editForm.name} onChange={(e: any) => setEditForm(f => ({ ...f, name: e.target.value }))} />
        <Input label="Description" value={editForm.description} onChange={(e: any) => setEditForm(f => ({ ...f, description: e.target.value }))} />
        <div className="flex items-center gap-2 mt-3">
          <input type="checkbox" checked={editForm.active} onChange={e => setEditForm(f => ({ ...f, active: e.target.checked }))} className="rounded border-gray-300" />
          <label className="text-sm text-gray-700">Active</label>
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="secondary" onClick={() => setEditModal({ open: false })}>Cancel</Button>
          <Button onClick={handleEditSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </div>
      </Modal>

      {/* New Price Modal */}
      <Modal open={newPriceModal.open} onClose={() => setNewPriceModal({ open: false })} title="Add Price">
        <Input label="Amount ($)" type="number" step="0.01" value={newPriceForm.amount} onChange={(e: any) => setNewPriceForm(f => ({ ...f, amount: e.target.value }))} />
        <div className="flex items-center gap-2 mt-3">
          <input type="checkbox" checked={newPriceForm.recurring} onChange={e => setNewPriceForm(f => ({ ...f, recurring: e.target.checked }))} className="rounded border-gray-300" />
          <label className="text-sm text-gray-700">Recurring</label>
        </div>
        {newPriceForm.recurring && (
          <div className="mt-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Interval</label>
            <select value={newPriceForm.interval} onChange={e => setNewPriceForm(f => ({ ...f, interval: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
              <option value="week">Weekly</option>
            </select>
          </div>
        )}
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="secondary" onClick={() => setNewPriceModal({ open: false })}>Cancel</Button>
          <Button onClick={handleNewPrice} disabled={saving}>{saving ? 'Creating...' : 'Create Price'}</Button>
        </div>
      </Modal>
    </div>
  );
};
